import type { ReactNode } from 'react'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'

interface Props {
  open: boolean
  title: string
  /** What will happen. */
  consequence: string
  /** What will *not* happen — usually the more reassuring half. */
  reassurance?: string
  /** Whether this can be undone, said plainly. */
  reversible: boolean
  /**
   * Whether this takes something away.
   *
   * A separate axis from reversibility, because they are separate facts.
   * Disabling a customer stops every one of their integrations the moment it
   * is confirmed and is also completely reversible; a wallet adjustment takes
   * nothing away and cannot be undone. Colouring by reversibility alone gave
   * the first one a calm primary button and the second an alarming red one,
   * which is backwards from the blast radius an operator is weighing.
   */
  destructive?: boolean
  confirmLabel: string
  busy?: boolean
  onConfirm: () => void
  onCancel: () => void
  children?: ReactNode
}

/**
 * One confirmation pattern for anything with consequences.
 *
 * A dialog that only asks "are you sure?" tells the operator nothing they did
 * not already know. This one states what changes, what does not, and whether
 * the change can be undone — which is usually the question actually being
 * asked.
 *
 * No typed-phrase gauntlet: everything it currently guards is reversible, and
 * friction spent on reversible actions is friction unavailable for the ones
 * that are not.
 */
export function ConfirmAction({
  open,
  title,
  consequence,
  reassurance,
  reversible,
  destructive = false,
  confirmLabel,
  busy = false,
  onConfirm,
  onCancel,
  children,
}: Props) {
  return (
    <Dialog open={open} onClose={busy ? undefined : onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText component="div">
          <p>{consequence}</p>
          {reassurance ? <p>{reassurance}</p> : null}
          <p>
            {reversible
              ? 'این عملیات قابل بازگشت است.'
              : 'این عملیات قابل بازگشت نیست.'}
          </p>
        </DialogContentText>
        {children}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={busy}>
          انصراف
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color={destructive ? 'error' : 'primary'}
          disabled={busy}
        >
          {busy ? 'در حال انجام…' : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
